/**
 * La scheda di una domanda di Aida (30-09-2026).
 *
 * Prima una domanda era un'etichetta attaccata a una mail: «Mi manca il
 * riferimento: ddt». Non diceva con chi fosse la conversazione, non si capiva
 * come chiuderla, e rispondere dal portale spesso non cambiava niente (9 scelte
 * su 9 senza l'email agganciata). Enrico: *le domande devono diventare un momento
 * di addestramento di Aida, non una causa di stress*.
 *
 * La scheda, dall'alto in basso:
 *  1. **di chi è la conversazione** — un chip per interlocutore, col ruolo; se
 *     Aida non lo sa, «Chi è?» (è quasi sempre il motivo per cui chiede);
 *  2. **cosa ho capito**;
 *  3. **cosa mi manca** — una domanda sola, discorsiva;
 *  4. **come rispondere** — opzioni, campi, testo libero, e sempre le tre uscite:
 *     nessuna di queste · non esiste / non si applica · non lo so ancora;
 *  5. **l'ultima risposta col suo resoconto**: cosa è cambiato, se basta, cosa
 *     manca ancora — con «Annulla» e «Aggiungi alle note».
 */

import {
  AlertTriangle,
  Check,
  CircleHelp,
  Clock,
  Eye,
  Loader2,
  Mail,
  Pencil,
  MessagesSquare,
  Paperclip,
  RotateCcw,
  SendHorizontal,
  Sparkles,
  StickyNote,
  Trash2,
  Undo2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CaricoPrevistoDrawer } from "@/components/m3/CaricoPrevistoDrawer";
import { ChiE } from "@/components/m3/ChiE";
import { CommessaDrawer } from "@/components/m3/CommessaDrawer";
import { ConversazioneDrawer } from "@/components/m3/ConversazioneDrawer";
import { PacchettoDrawer } from "@/components/m3/PacchettoDrawer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type PercheNonPertinente,
  useAnnullaRisposta,
  useChiudiDomanda,
  useCpPatchRiga,
  useRiprendiDomanda,
  useRispondiDomanda,
  useRispostaNelleNote,
  useUscitaDomanda,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type {
  CaricoPrevistoPatch,
  DomandaAida,
  DomandaEmail,
  EsitoDomanda,
  EsitoRisposta,
  Interlocutore,
  RuoloDomanda,
  RuoloIndirizzo,
  TentativoRisposta,
} from "@/types/api";

export const ETICHETTA_TIPO: Record<string, string> = {
  scelta_candidato: "A quale pratica",
  domanda_aperta: "Dato mancante",
  conferma_cliente: "Conferma cliente",
};

const ESITO: Record<EsitoRisposta, { label: string; cls: string }> = {
  esaustiva: { label: "Risposta completa", cls: "bg-emerald-100 text-emerald-800" },
  parziale: { label: "Risposta parziale", cls: "bg-amber-100 text-amber-800" },
  negativa: { label: "Risposta: non c'è", cls: "bg-slate-200 text-slate-700" },
  rinvio: { label: "Rinviata", cls: "bg-sky-100 text-sky-800" },
  non_capita: { label: "Non capita", cls: "bg-red-100 text-red-700" },
};

const CANALE: Record<string, string> = {
  portale: "dal portale",
  chat: "in chat",
  email: "per email",
  aida: "da sola",
};

const RUOLO_CLS: Record<string, string> = {
  cliente: "border-emerald-300 bg-emerald-50 text-emerald-900",
  spedizioniere: "border-sky-300 bg-sky-50 text-sky-900",
  fornitore: "border-violet-300 bg-violet-50 text-violet-900",
  collega: "border-slate-300 bg-slate-50 text-slate-700",
  altro: "border-slate-300 bg-slate-50 text-slate-700",
  sconosciuto: "border-dashed border-amber-400 bg-amber-50 text-amber-900",
};

function dataOra(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Il testo che apre la chat: la domanda più una riga su da dove viene. */
export function testoPerChat(d: DomandaAida): string {
  const corpo = d.testo_domanda || d.spiegazione || d.testo;
  const e = d.email;
  if (!e) return corpo;
  const pezzi = [
    dataOra(e.ricevuta_il) && `email del ${dataOra(e.ricevuta_il)}`,
    e.da && `da ${e.da}`,
    e.a && `a ${e.a}`,
    e.oggetto && `oggetto «${e.oggetto}»`,
    e.allegati > 0 && `${e.allegati} ${e.allegati === 1 ? "allegato" : "allegati"}`,
  ].filter(Boolean);
  return pezzi.length ? `${corpo}\n\n(${pezzi.join(", ")})` : corpo;
}

/** Da dove arriva la domanda: chi, quando, con che oggetto, con quanti allegati. */
export function ContestoEmail({ email }: { email: DomandaEmail | null }) {
  if (!email) return null;
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
      <Mail className="h-3 w-3 shrink-0" />
      {dataOra(email.ricevuta_il) && <span>{dataOra(email.ricevuta_il)}</span>}
      {email.da && <span className="truncate">· da {email.da}</span>}
      {email.a && <span className="truncate">· a {email.a}</span>}
      {email.oggetto && <span className="truncate">· «{email.oggetto}»</span>}
      {email.allegati > 0 && (
        <span className="inline-flex items-center gap-0.5">
          <Paperclip className="h-3 w-3" /> {email.allegati}
        </span>
      )}
    </p>
  );
}

/** Il resoconto di una risposta: cosa è cambiato, se basta, cosa manca. */
function Resoconto({
  d,
  t,
  onFatto,
}: {
  d: DomandaAida;
  t: TentativoRisposta;
  onFatto: (m: string) => void;
}) {
  const annulla = useAnnullaRisposta(d.id);
  const note = useRispostaNelleNote(d.id);
  const esito = t.esito ? ESITO[t.esito] : null;
  return (
    <div className={cn("rounded-md border bg-muted/30 p-2 text-xs", t.annullato_il && "opacity-60")}>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
        <span>
          {dataOra(t.quando)} · {t.canale === "aida" ? "Aida" : "hai risposto"}{" "}
          {CANALE[t.canale] ?? t.canale}
        </span>
        {esito && (
          <span className={cn("rounded-full px-1.5 py-0.5 font-semibold", esito.cls)}>
            {esito.label}
          </span>
        )}
        {t.annullato_il && <span className="font-medium">· annullata</span>}
      </div>
      {t.testo && t.canale !== "aida" && (
        <p className="mt-1 whitespace-pre-line text-foreground">«{t.testo}»</p>
      )}
      {t.effetti.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {t.effetti.map((e, n) => (
            <li key={n} className="flex items-start gap-1">
              <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
              <span>{e.testo}</span>
            </li>
          ))}
        </ul>
      ) : t.esito === "non_capita" ? (
        <p className="mt-1 flex items-start gap-1 text-red-700">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          Non l'ho capita{t.motivo ? `: ${t.motivo}` : ""}. Prova a dirla in un altro modo, o usa
          una delle uscite qui sotto.
        </p>
      ) : null}
      {t.interpretazione?.includes("mancano ancora") && (
        <p className="mt-1 text-amber-800">
          Manca ancora: {t.interpretazione.split("mancano ancora:")[1]?.trim()}
        </p>
      )}
      {!t.annullato_il && (t.annullabile || t.nota_disponibile) && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {t.nota_disponibile && (
            <button
              type="button"
              disabled={note.isPending}
              onClick={() => note.mutate(t.id, { onSuccess: (r) => onFatto(r.effetto) })}
              className="inline-flex items-center gap-1 rounded border bg-card px-1.5 py-0.5 text-[11px] hover:bg-muted disabled:opacity-50"
            >
              <StickyNote className="h-3 w-3" /> Aggiungi alle note
            </button>
          )}
          {t.annullabile && (
            <button
              type="button"
              disabled={annulla.isPending}
              onClick={() =>
                annulla.mutate(t.id, {
                  onSuccess: () => onFatto("Risposta annullata: la domanda è tornata com'era."),
                })
              }
              className="inline-flex items-center gap-1 rounded border bg-card px-1.5 py-0.5 text-[11px] hover:bg-muted disabled:opacity-50"
            >
              <Undo2 className="h-3 w-3" /> Annulla
            </button>
          )}
          {(annulla.isError || note.isError) && (
            <span className="text-[11px] text-red-600">
              {((annulla.error || note.error) as Error)?.message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function messaggioEsito(r: EsitoDomanda): string {
  const fatti = r.effetti.join(" · ");
  if (!r.capita) return r.motivo ? `Non l'ho capita: ${r.motivo}.` : "Non l'ho capita.";
  if (r.esito === "rinvio") return fatti || "Rinviata di qualche giorno.";
  if (r.esito === "parziale")
    return `${fatti ? `${fatti}. ` : ""}Manca ancora: ${r.campi_residui.join(", ")}.`;
  return fatti ? `Fatto: ${fatti}.` : "Risposta registrata.";
}

/** Chi è l'interlocutore, in parole: niente «?» — se Aida non è sicura lo dice. */
function comeLoVede(i: Interlocutore): string {
  if (i.ruolo === "sconosciuto") return "da riconoscere";
  if (i.certezza === "debole") return `possibile ${i.ruolo_label}`;
  return i.ruolo_label;
}

/** Una riga della lista degli interlocutori: uno sotto l'altro, con la matita. */
export function RigaInterlocutore({
  i,
  aperto,
  onApri,
}: {
  i: Interlocutore;
  aperto: boolean;
  onApri: () => void;
}) {
  const chi =
    i.ruolo === "cliente"
      ? i.cliente_nome
      : i.ruolo === "spedizioniere"
        ? i.trasportatore_nome
        : i.etichetta;
  const nome = chi || i.nome;
  const ignoto = i.ruolo === "sconosciuto";
  return (
    <li className="flex items-center gap-2 py-1 text-xs">
      <span
        className={cn(
          "w-40 shrink-0 truncate rounded-full border px-2 py-0.5 text-center text-[10.5px] font-semibold",
          RUOLO_CLS[i.ruolo] ?? RUOLO_CLS.altro,
          i.certezza === "debole" && !ignoto && "border-dashed",
        )}
        title={i.certezza === "debole" ? "Riconosciuto solo dal dominio dell'indirizzo" : undefined}
      >
        {comeLoVede(i)}
      </span>
      <span className="min-w-0 flex-1 truncate">
        {nome && <span className="font-medium">{nome} </span>}
        {/* L'indirizzo si vede SEMPRE: è la cosa su cui si risponde «Chi è?». */}
        <span className={cn(nome && "text-muted-foreground")}>{i.email}</span>
        {/* l'indizio dell'associazione di luglio, per esteso e non tagliato */}
        {i.ruolo === "spedizioniere" && i.cliente_nome && !i.trasportatore_nome && (
          <span className="text-muted-foreground"> · di solito lavora per {i.cliente_nome}</span>
        )}
      </span>
      {ignoto ? (
        <button
          type="button"
          onClick={onApri}
          className={cn(
            "shrink-0 rounded-md bg-amber-200/80 px-2 py-0.5 text-[11px] font-semibold text-amber-950 hover:bg-amber-300",
            aperto && "ring-2 ring-amber-400",
          )}
        >
          Chi è?
        </button>
      ) : (
        <button
          type="button"
          onClick={onApri}
          title="Cambia chi è"
          aria-label={`Cambia chi è ${i.email}`}
          className={cn(
            "shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground",
            aperto && "bg-muted text-foreground",
          )}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

type Conferma =
  | { tipo: "opzione"; indice: number; riferimento: string; apri: ReturnType<typeof apribile> }
  | { tipo: "nessuna" }
  | { tipo: "non_esiste" };

export function SchedaDomanda({
  d,
  onApriChat,
  onFatto,
  sospesa = false,
}: {
  d: DomandaAida;
  onApriChat?: (d: DomandaAida, testoPerLaChat: string) => void;
  /** `annullabile`: il tentativo appena registrato, per il «Annulla» del messaggio. */
  onFatto: (m: string, annullabile?: { domandaId: string; tentativoId: string }) => void;
  sospesa?: boolean;
}) {
  const riprendi = useRiprendiDomanda(d.id);
  const [chiE, setChiE] = useState<{ email: string; ruolo?: Exclude<RuoloIndirizzo, "sconosciuto"> } | null>(null);
  const [archivia, setArchivia] = useState(false);
  // Cosa è aperto sopra la scheda: la conversazione, una riga, un pacchetto
  // (NDP) o una commessa — «devo poter verificare di cosa si parla» (30-09).
  const [aperto, setAperto] = useState<{ tipo: "conversazione" } | Apribile | null>(null);

  const vecchia = d.giorni >= 14;
  const sconosciuti = d.controparte.filter((i) => i.ruolo === "sconosciuto");
  const clienteNoto = d.controparte.find((i) => i.ruolo === "cliente" && i.cliente_codice);
  // Chi si può confermare come spedizioniere dalla card: un indirizzo di
  // spedizioniere senza azienda, o uno sconosciuto.
  const candidatoSped =
    d.controparte.find((i) => i.ruolo === "spedizioniere" && !i.trasportatore_nome) ??
    sconosciuti[0] ??
    null;
  const candidatoCliente = sconosciuti[0] ?? null;

  const testoDomanda = d.testo_domanda || d.spiegazione || d.testo;

  return (
    <li className="rounded-lg border bg-card p-3" data-domanda={d.id}>
      {/* intestazione, con le due azioni che servono per fare mente locale:
          sono la prima cosa che Marina fa, quindi stanno in cima (30-09) */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span className="rounded-full border px-1.5 py-0.5 font-medium">
            {ETICHETTA_TIPO[d.tipo] ?? d.tipo}
          </span>
          {d.riferimento && <span className="font-medium text-foreground">{d.riferimento}</span>}
          {d.cliente && <span>· {d.cliente}</span>}
          <span className={cn(vecchia && "font-semibold text-amber-700")}>
            · {d.giorni === 0 ? "oggi" : `${d.giorni} gg fa`}
          </span>
          {sospesa && d.sospesa_fino_il && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-1.5 py-0.5 font-medium text-sky-800">
              <Clock className="h-3 w-3" /> torna il{" "}
              {new Date(d.sospesa_fino_il).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {d.email && (
            <button
              type="button"
              onClick={() => setAperto({ tipo: "conversazione" })}
              title="Rileggi le mail da cui nasce la domanda"
              className="inline-flex items-center gap-1.5 rounded-md border border-brand-300 bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-800 hover:bg-brand-100"
            >
              <MessagesSquare className="h-3.5 w-3.5" /> Rivedi la conversazione
            </button>
          )}
          {onApriChat && (
            <button
              type="button"
              onClick={() => onApriChat(d, testoPerChat(d))}
              title="Parlane con Aida in chat"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-800"
            >
              <Sparkles className="h-3.5 w-3.5" /> Chiedi ad Aida
            </button>
          )}
        </div>
      </div>

      {/* 1. di chi è la conversazione: uno sotto l'altro */}
      <Blocco titolo="Con chi è la conversazione">
        {d.direzione === "interna" && (
          <p className="mb-0.5 text-xs text-muted-foreground">
            Solo persone di Mister Pet: è una conversazione fra colleghi.
          </p>
        )}
        <ul className="divide-y rounded-md border px-2">
          {d.colleghi.map((c) => (
            <li key={c.email} className="flex items-center gap-2 py-1 text-xs">
              <span className="w-40 shrink-0 truncate rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-center text-[10.5px] font-semibold text-slate-700">
                {c.campo === "from" ? "scrive (Mister Pet)" : "collega"}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {(c.etichetta || c.nome) && <span className="font-medium">{c.etichetta || c.nome} </span>}
                <span className={cn((c.etichetta || c.nome) && "text-muted-foreground")}>{c.email}</span>
              </span>
            </li>
          ))}
          {d.controparte.map((i) => (
            <RigaInterlocutore
              key={i.email}
              i={i}
              aperto={chiE?.email === i.email}
              onApri={() => setChiE(chiE?.email === i.email ? null : { email: i.email })}
            />
          ))}
          {d.direzione !== "interna" && d.controparte.length === 0 && d.colleghi.length === 0 && (
            <li className="py-1 text-xs text-muted-foreground">Nessun interlocutore nella mail.</li>
          )}
        </ul>
        {chiE && (
          <ChiE
            email={chiE.email}
            domandaId={d.id}
            ruoloIniziale={
              chiE.ruolo ??
              (() => {
                const r = d.controparte.find((i) => i.email === chiE.email)?.ruolo;
                return r && r !== "sconosciuto" ? r : undefined;
              })()
            }
            clienteSuggerito={
              clienteNoto
                ? { codice: clienteNoto.cliente_codice!, nome: clienteNoto.cliente_nome ?? "" }
                : null
            }
            onAnnulla={() => setChiE(null)}
            onFatto={(msg) => {
              setChiE(null);
              onFatto(msg.length ? `Fatto: ${msg.join(" · ")}.` : "Salvato.");
            }}
          />
        )}
      </Blocco>

      {/* 2. chi e cosa: «tagliamo la testa al toro» (Enrico, 30-09). Le card da
          completare si cliccano: è lì che viene da cliccare. */}
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <Ruolo
          titolo="Cliente"
          r={d.ruolo_cliente}
          azione={
            d.ruolo_cliente?.stato !== "noto" && candidatoCliente
              ? {
                  testo: `È ${candidatoCliente.email}?`,
                  fai: () => setChiE({ email: candidatoCliente.email, ruolo: "cliente" }),
                }
              : undefined
          }
        />
        <Ruolo
          titolo="Spedizioniere"
          r={d.ruolo_spedizioniere}
          azione={
            d.ruolo_spedizioniere?.stato !== "noto" && d.ruolo_spedizioniere?.stato !== "cliente_stesso"
              ? candidatoSped
                ? {
                    testo: `Conferma chi è ${candidatoSped.email}`,
                    fai: () => setChiE({ email: candidatoSped.email, ruolo: "spedizioniere" }),
                  }
                : d.carico_id && d.campi.length > 0
                  ? { testo: "Indicalo sulla riga (Chi ritira)", fai: () => setAperto({ tipo: "avviso", id: d.carico_id! }) }
                  : undefined
              : undefined
          }
        />
        <div className="rounded-md border bg-muted/20 px-2 py-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Commessa
          </div>
          {d.commesse.length === 0 ? (
            <div className="text-xs text-muted-foreground">non indicata</div>
          ) : (
            <div className="mt-0.5 flex flex-wrap gap-1">
              {d.commesse.map((c) => (
                <button
                  key={c.numero}
                  type="button"
                  onClick={() => setAperto({ tipo: "commessa", numero: c.numero })}
                  title="Apri la commessa"
                  className="inline-flex items-center gap-1 rounded border bg-card px-1.5 py-0.5 text-xs font-semibold tabular-nums hover:border-brand-400 hover:text-brand-700"
                >
                  <Eye className="h-3 w-3 text-muted-foreground" />
                  {c.numero}
                  {c.riferimento_cliente && (
                    <span className="max-w-[9rem] truncate text-[10.5px] font-normal text-muted-foreground">
                      · {c.riferimento_cliente}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {d.commesse.some((c) => c.da_rif_cliente) && (
            <div className="mt-0.5 text-[10.5px] text-muted-foreground">
              riconosciuta dal numero d'ordine del cliente
            </div>
          )}
        </div>
      </div>

      {/* 3. cosa mi manca */}
      <Blocco titolo="Cosa mi manca">
        <p className="whitespace-pre-line text-sm">{testoDomanda}</p>
      </Blocco>

      {/* 4. come rispondere, e 5. l'ultima risposta: lo stesso blocco del drawer */}
      <RispondiDomanda d={d} onFatto={onFatto} onApri={setAperto} sospesa={sospesa} />

      {/* piede: da dove viene, e le uscite meno frequenti */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t pt-2">
        <div className="min-w-0 flex-1">
          <ContestoEmail email={d.email} />
        </div>
        {sospesa && (
          <button
            type="button"
            disabled={riprendi.isPending}
            onClick={() => riprendi.mutate(undefined, { onSuccess: () => onFatto("Domanda ripresa: è tornata fra le aperte.") })}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium hover:bg-muted"
          >
            <RotateCcw className="h-3 w-3" /> Riprendi ora
          </button>
        )}
        <button
          type="button"
          onClick={() => setArchivia(true)}
          title="Non serve più: archivia"
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-red-600"
        >
          <Trash2 className="h-3 w-3" /> Non serve più
        </button>
      </div>

      {aperto?.tipo === "conversazione" && (
        <ConversazioneDrawer
          domandaId={d.id}
          titolo={d.email?.oggetto ?? d.testo}
          onClose={() => setAperto(null)}
        />
      )}
      {aperto?.tipo === "commessa" && (
        <CommessaDrawer
          numero={aperto.numero}
          onClose={() => setAperto(null)}
          onApriRiga={(id) => setAperto({ tipo: "avviso", id })}
        />
      )}
      {aperto?.tipo === "avviso" && (
        <CaricoPrevistoDrawer
          id={aperto.id}
          onClose={() => setAperto(null)}
          onApriPlico={(caricoId) => setAperto({ tipo: "carico", id: caricoId })}
        />
      )}
      {aperto?.tipo === "carico" && (
        <PacchettoDrawer caricoId={aperto.id} onClose={() => setAperto(null)} />
      )}

      {archivia && (
        <DialogArchivia
          d={d}
          onChiudi={() => setArchivia(false)}
          onFatto={(m) => {
            setArchivia(false);
            onFatto(m);
          }}
        />
      )}
    </li>
  );
}


/** Cosa si può aprire per guardare prima di rispondere. */
export type Apribile =
  | { tipo: "avviso"; id: string }
  | { tipo: "carico"; id: string }
  | { tipo: "commessa"; numero: number };

/**
 * «Rispondi» e «Ultima risposta»: le risposte rapide (con la conferma), le tre
 * uscite, i campi, il testo libero, e il resoconto con «Annulla». Uno solo per la
 * pagina delle domande e per il drawer della riga (30-09-2026, quarto giro): lo
 * stesso gesto deve rispondere nello stesso modo dovunque lo si trovi.
 *
 * `conCampi={false}` nel drawer: lì i campi chiesti sono già nella sezione del
 * ritiro, evidenziati — ripeterli qui sarebbe la seconda copia dello stesso dato.
 */
export function RispondiDomanda({
  d,
  onFatto,
  onApri,
  sospesa = false,
  conCampi = true,
}: {
  d: DomandaAida;
  onFatto: (m: string, annullabile?: { domandaId: string; tentativoId: string }) => void;
  onApri: (a: Apribile) => void;
  sospesa?: boolean;
  conCampi?: boolean;
}) {
  const rispondi = useRispondiDomanda(d.id);
  const uscita = useUscitaDomanda(d.id);
  const patchRiga = useCpPatchRiga();
  const qc = useQueryClient();
  const [testo, setTesto] = useState("");
  const [conferma, setConferma] = useState<Conferma | null>(null);
  const [campi, setCampi] = useState<Record<string, string>>({});
  const [esitoLocale, setEsitoLocale] = useState<string | null>(null);
  const occupato = rispondi.isPending || uscita.isPending || patchRiga.isPending;
  const ultimo = d.tentativi.at(-1);
  const setAperto = onApri;

  function dopo(r: EsitoDomanda) {
    const m = messaggioEsito(r);
    setEsitoLocale(r.chiusa ? null : m);
    if (r.capita) setTesto("");
    onFatto(
      r.chiusa ? `${m} La domanda è chiusa.` : m,
      r.capita && r.tentativo_id ? { domandaId: d.id, tentativoId: r.tentativo_id } : undefined,
    );
  }

  function invia(t: string) {
    if (!t.trim()) return;
    rispondi.mutate({ testo: t.trim() }, { onSuccess: dopo });
  }

  function confermato() {
    const c = conferma;
    setConferma(null);
    if (!c) return;
    if (c.tipo === "opzione") invia(String(c.indice));
    else uscita.mutate({ uscita: c.tipo, nota: testo || null }, { onSuccess: dopo });
  }

  function salvaCampi() {
    if (!d.carico_id) return;
    const body: CaricoPrevistoPatch = {};
    for (const [k, v] of Object.entries(campi)) {
      if (!v) continue;
      if (k === "pallet") body.pallet = Number(v);
      if (k === "data_ritiro") body.data_ritiro = v;
      if (k === "pronto_dal") body.pronto_dal = v;
    }
    patchRiga.mutate(
      { id: d.carico_id, body },
      {
        onSuccess: () => {
          setCampi({});
          // la riga cambia, e con lei la domanda (i campi che mancano ancora)
          qc.invalidateQueries({ queryKey: ["m3", "domande"] });
          onFatto("Scritto sulla riga del tabellone.");
        },
      },
    );
  }

  return (
    <>
      {/* 4. come rispondere: prima le risposte rapide (le più usate), poi il testo */}
      {!sospesa && (
        <div className="mt-2 space-y-2 rounded-md border bg-muted/20 p-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Rispondi
          </div>
          {d.opzioni.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {d.opzioni.map((o, n) => (
                <span key={n} className="inline-flex items-stretch">
                  <button
                    type="button"
                    disabled={occupato}
                    onClick={() =>
                      setConferma({
                        tipo: "opzione",
                        indice: Number(o.indice ?? n + 1),
                        riferimento:
                          (o.riferimento ?? "") +
                          (o.riferimento_cliente ? ` («${o.riferimento_cliente}» per il cliente)` : ""),
                        apri: apribile(o),
                      })
                    }
                    title="È questa"
                    className="rounded-l-md border border-brand-300 bg-card px-2 py-1 text-left text-xs hover:bg-brand-50 disabled:opacity-50"
                  >
                    <b>{String(o.indice ?? n + 1)}.</b> {o.riferimento}
                    {o.fatto && <span className="ml-1 text-[11px] text-muted-foreground">— {o.fatto}</span>}
                    {/* come la chiama il cliente: è il numero delle sue mail */}
                    {o.riferimento_cliente && (
                      <span className="block text-[10.5px] text-muted-foreground">
                        per il cliente: {o.riferimento_cliente}
                      </span>
                    )}
                  </button>
                  {/* Guardarla prima di sceglierla: la riga, il pacchetto con la
                      NDP, o la commessa (30-09). */}
                  {apribile(o) ? (
                    <button
                      type="button"
                      onClick={() => setAperto(apribile(o)!)}
                      title="Guardala prima di scegliere"
                      aria-label={`Apri ${o.riferimento}`}
                      className="rounded-r-md border border-l-0 border-brand-300 bg-card px-1.5 text-brand-700 hover:bg-brand-50"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <span className="w-0 rounded-r-md border border-l-0 border-brand-300" />
                  )}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            {d.opzioni.length > 0 && (
              <Uscita testo="Nessuna di queste" disabled={occupato} onClick={() => setConferma({ tipo: "nessuna" })} />
            )}
            <Uscita testo="Non esiste / non si applica" disabled={occupato} onClick={() => setConferma({ tipo: "non_esiste" })} />
            <Uscita
              testo="Non lo so ancora"
              disabled={occupato}
              onClick={() => uscita.mutate({ uscita: "non_lo_so", nota: testo || null }, { onSuccess: dopo })}
            />
          </div>

          {conCampi && d.campi.length > 0 && d.carico_id && (
            <div className="flex flex-wrap items-end gap-2">
              {d.campi.map((c) => (
                <label key={c.chiave} className="text-[11px] text-muted-foreground">
                  {c.label}
                  <input
                    type={c.chiave === "pallet" ? "number" : "date"}
                    min={c.chiave === "pallet" ? "0" : undefined}
                    value={campi[c.chiave] ?? ""}
                    onChange={(e) => setCampi((x) => ({ ...x, [c.chiave]: e.target.value }))}
                    className="mt-0.5 block h-8 rounded-md border-2 border-amber-300 bg-amber-50 px-2 text-xs"
                  />
                </label>
              ))}
              <button
                type="button"
                disabled={occupato || !Object.values(campi).some(Boolean)}
                onClick={salvaCampi}
                className="h-8 rounded-md border bg-card px-2.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                Scrivi sulla riga
              </button>
            </div>
          )}

          <AreaRisposta
            valore={testo}
            onChange={setTesto}
            onInvia={() => !occupato && invia(testo)}
            inCorso={rispondi.isPending}
            placeholder={
              d.opzioni.length
                ? "…oppure scrivi il riferimento giusto, o spiegalo come lo diresti a voce"
                : "Scrivi la risposta come la diresti a voce…"
            }
          />
          {(rispondi.isError || uscita.isError || patchRiga.isError) && (
            <p className="text-[11px] text-red-600">
              {((rispondi.error || uscita.error || patchRiga.error) as Error)?.message ??
                "Non riuscito."}
            </p>
          )}
          {esitoLocale && <p className="text-[11px] font-medium text-amber-800">{esitoLocale}</p>}
        </div>
      )}

      {/* 5. l'ultima risposta */}
      {ultimo && (
        <Blocco titolo={d.tentativi.length > 1 ? `Ultima risposta (di ${d.tentativi.length})` : "Ultima risposta"}>
          <Resoconto d={d} t={ultimo} onFatto={onFatto} />
        </Blocco>
      )}

      {conferma && (
        <DialogConferma
          c={conferma}
          d={d}
          onGuarda={(a) => {
            setConferma(null);
            setAperto(a);
          }}
          onConferma={confermato}
          onAnnulla={() => setConferma(null)}
        />
      )}

    </>
  );
}

/**
 * La risposta scritta: un'area che cresce con il testo (da 2 a 6 righe), col
 * pulsante d'invio dentro, come nella chat di Aida — Invio manda, Maiusc+Invio
 * va a capo. Una riga sola costringeva le spiegazioni in un filo (Enrico, 30-09).
 */
function AreaRisposta({
  valore,
  onChange,
  onInvia,
  inCorso,
  placeholder,
}: {
  valore: string;
  onChange: (v: string) => void;
  onInvia: () => void;
  inCorso: boolean;
  placeholder: string;
}) {
  return (
    <div className="relative rounded-md border-2 border-border bg-card focus-within:border-brand-400">
      <textarea
        value={valore}
        rows={2}
        onChange={(e) => {
          onChange(e.target.value);
          const el = e.currentTarget;
          el.style.height = "auto";
          el.style.height = `${Math.min(el.scrollHeight, 6 * 20)}px`;
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (valore.trim()) onInvia();
          }
        }}
        placeholder={placeholder}
        className="block w-full resize-none bg-transparent px-2.5 py-2 pr-24 text-xs leading-5 focus:outline-none"
      />
      <div className="pointer-events-none absolute bottom-1.5 right-1.5 flex items-center gap-2">
        <span className="hidden text-[10px] text-muted-foreground sm:inline">Invio ↵</span>
        <button
          type="button"
          disabled={!valore.trim() || inCorso}
          onClick={onInvia}
          className="pointer-events-auto inline-flex h-7 items-center gap-1 rounded-md bg-brand-700 px-2.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-40"
        >
          {inCorso ? <Loader2 className="h-3 w-3 animate-spin" /> : <SendHorizontal className="h-3 w-3" />}
          Invia
        </button>
      </div>
    </div>
  );
}

/** Prima di chiudere la domanda: cosa succederà, e la possibilità di guardare. */
function DialogConferma({
  c,
  d,
  onGuarda,
  onConferma,
  onAnnulla,
}: {
  c: Conferma;
  d: DomandaAida;
  onGuarda: (a: NonNullable<ReturnType<typeof apribile>>) => void;
  onConferma: () => void;
  onAnnulla: () => void;
}) {
  const oggetto = d.email?.oggetto ? `«${d.email.oggetto}»` : "questa email";
  const titolo =
    c.tipo === "opzione"
      ? `È ${c.riferimento}?`
      : c.tipo === "nessuna"
        ? "Nessuna delle pratiche proposte?"
        : "Non esiste, o non si applica?";
  const testo =
    c.tipo === "opzione"
      ? `Aida aggancia ${oggetto} a ${c.riferimento}: la ritroverai nella storia di quella pratica, e da lì in poi le mail di questa conversazione andranno lì.`
      : c.tipo === "nessuna"
        ? `La domanda si chiude e ${oggetto} non viene agganciata a nessuna pratica. Se sai qual è, scrivila invece nel campo della risposta.`
        : "La domanda si chiude, e Aida non chiederà più questo dato su questa riga.";
  return (
    <Dialog open onOpenChange={(v) => !v && onAnnulla()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titolo}</DialogTitle>
          <DialogDescription>{testo}</DialogDescription>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground">
          Se ti sbagli, dopo c'è «Annulla».
        </p>
        <DialogFooter className="gap-2 sm:justify-between">
          {c.tipo === "opzione" && c.apri ? (
            <button
              type="button"
              onClick={() => onGuarda(c.apri!)}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              <Eye className="h-4 w-4" /> Guardala prima
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onAnnulla}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={onConferma}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
            >
              Conferma
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Cosa si apre guardando un'opzione: la riga, il pacchetto (NDP) o la commessa. */
function apribile(o: DomandaAida["opzioni"][number]):
  | { tipo: "avviso"; id: string }
  | { tipo: "carico"; id: string }
  | { tipo: "commessa"; numero: number }
  | null {
  if (!o.id) return null;
  if (o.tipo === "avviso") return { tipo: "avviso", id: String(o.id) };
  if (o.tipo === "carico") return { tipo: "carico", id: String(o.id) };
  if (o.tipo === "commessa" && /^\d+$/.test(String(o.id))) return { tipo: "commessa", numero: Number(o.id) };
  return null;
}

const RUOLO_STATO: Record<string, string> = {
  noto: "",
  cliente_stesso: "",
  non_indicato: "non indicato",
  da_riconoscere: "da riconoscere",
};

/** Una cella della testata: chi è, e da dove Aida lo sa. Se è da completare e
 *  c'è un modo per farlo, la cella si clicca — è lì che viene da cliccare. */
function Ruolo({
  titolo,
  r,
  azione,
}: {
  titolo: string;
  r: RuoloDomanda | null;
  azione?: { testo: string; fai: () => void };
}) {
  const noto = r && (r.stato === "noto" || r.stato === "cliente_stesso");
  const corpo = (
    <>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{titolo}</div>
      <div
        className={cn("truncate text-xs", noto ? "font-semibold" : azione ? "text-amber-900" : "text-muted-foreground")}
        title={r?.nome ?? undefined}
      >
        {noto ? r!.nome : r?.nome ? `${RUOLO_STATO[r.stato]}: ${r.nome}` : RUOLO_STATO[r?.stato ?? "non_indicato"]}
      </div>
      {r?.come && <div className="truncate text-[10.5px] text-muted-foreground">{r.come}</div>}
      {azione && (
        <div className="mt-0.5 truncate text-[11px] font-semibold text-brand-700">{azione.testo} →</div>
      )}
    </>
  );
  // Giallo solo se c'è qualcosa da fare: una card gialla che non si clicca
  // invita a cliccare e non risponde (Enrico, 30-09).
  const cls = cn(
    "rounded-md border px-2 py-1.5 text-left",
    noto || !azione ? "bg-muted/20" : "border-dashed border-amber-300 bg-amber-50/50",
  );
  return azione ? (
    <button
      type="button"
      onClick={azione.fai}
      className={cn(cls, "transition-colors hover:border-solid hover:border-brand-400 hover:bg-brand-50/60")}
    >
      {corpo}
    </button>
  ) : (
    <div className={cls}>{corpo}</div>
  );
}

function Blocco({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div className="mt-2">
      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {titolo}
      </div>
      {children}
    </div>
  );
}

function Uscita({
  testo,
  disabled,
  onClick,
}: {
  testo: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-0.5 font-medium hover:bg-muted disabled:opacity-50"
    >
      <CircleHelp className="h-3 w-3" /> {testo}
    </button>
  );
}

const PERCHE: { id: PercheNonPertinente; label: string; aiuto?: string }[] = [
  { id: "era_collega", label: "Era una collega / un collega", aiuto: "lo segno in rubrica" },
  { id: "era_spedizioniere", label: "Era uno spedizioniere", aiuto: "lo segno in rubrica" },
  { id: "era_fornitore", label: "Era un fornitore", aiuto: "lo segno in rubrica" },
  { id: "info_nella_mail", label: "L'informazione era già nella mail" },
  { id: "non_riguarda_spedizioni", label: "Non riguarda le spedizioni" },
  { id: "altro", label: "Altro" },
];

/** «Non serve più»: superata, oppure «non dovevi chiedermelo» — e perché. */
function DialogArchivia({
  d,
  onChiudi,
  onFatto,
}: {
  d: DomandaAida;
  onChiudi: () => void;
  onFatto: (m: string) => void;
}) {
  const chiudi = useChiudiDomanda(d.id);
  const [passo, setPasso] = useState<"motivo" | "perche">("motivo");
  const [nota, setNota] = useState("");

  function manda(motivo: "superata" | "non_pertinente", perche?: PercheNonPertinente) {
    chiudi.mutate(
      { motivo, perche: perche ?? null, nota: nota.trim() || null },
      {
        onSuccess: (r) =>
          onFatto(
            motivo === "superata"
              ? "Domanda archiviata."
              : `Archiviata: Aida non doveva chiederlo.${
                  r.appreso.length ? ` Ho imparato: ${r.appreso.join(" · ")}.` : ""
                }`,
          ),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{passo === "motivo" ? "Questa domanda non serve più?" : "Perché non era da chiedere?"}</DialogTitle>
          <DialogDescription>
            {passo === "motivo"
              ? "Esce dalla lista senza risposta. La ritrovi nell'archivio e da lì puoi riaprirla."
              : "È così che Aida impara a non chiederlo più."}
          </DialogDescription>
        </DialogHeader>
        {passo === "motivo" ? (
          <div className="space-y-2">
            <button
              type="button"
              disabled={chiudi.isPending}
              onClick={() => manda("superata")}
              className="w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
            >
              <span className="font-medium">Superata</span>
              <span className="block text-xs text-muted-foreground">
                Non ha più oggetto: il carico è partito, la cosa si è già sistemata.
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPasso("perche")}
              className="w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-muted"
            >
              <span className="font-medium">Non dovevi chiedermelo</span>
              <span className="block text-xs text-muted-foreground">
                Aida ha sbagliato a domandarlo: dimmi perché e lo impara.
              </span>
            </button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {PERCHE.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={chiudi.isPending}
                onClick={() => manda("non_pertinente", p.id)}
                className="w-full rounded-md border px-3 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-50"
              >
                {p.label}
                {p.aiuto && <span className="ml-1 text-xs text-muted-foreground">— {p.aiuto}</span>}
              </button>
            ))}
            <input
              type="text"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Una nota per Aida (facoltativa)"
              className="h-8 w-full rounded-md border-2 border-border bg-card px-2 text-xs"
            />
          </div>
        )}
        {chiudi.isError && (
          <p className="text-xs text-red-600">{(chiudi.error as Error)?.message}</p>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={passo === "perche" ? () => setPasso("motivo") : onChiudi}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
          >
            {passo === "perche" ? "Indietro" : "Annulla"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
